import { ENEMY_KINDS, type EnemyKind } from '../data/enemies'
import { gameBridge } from '../events/GameBridge'
import { MAP_DEFINITIONS, MAP_IDS, type MapId } from '../data/maps'
import { CONTRACTS, type ContractId } from '../data/contracts'
import { ACHIEVEMENTS, ACHIEVEMENT_IDS, getDailyOperation, isConsecutiveOperation, type AchievementId } from '../data/operations'
import { emptyRunTelemetry, type RunTelemetry } from '../data/analytics'
import { LOADOUT_IDS, type LoadoutId } from '../data/loadouts'
import { CAMPAIGN_CHAPTERS, CAMPAIGN_ORDER } from '../data/campaignChapters'
import { getDailyChallenge, type RunMode } from '../data/endgame'

export type PerformanceRank = 'S' | 'A' | 'B' | 'C' | 'D'

const STORAGE_KEY = 'block-defense.profile.v1'

export interface PlayerProfile {
  readonly level: number
  readonly xp: number
  readonly totalKills: number
  readonly runs: number
  readonly wins: number
  readonly bestWave: number
  readonly bestScore: number
  readonly completedContracts: number
  readonly contractStreak: number
  readonly bestContractStreak: number
  readonly lastOperationId: string | null
  readonly operationStreak: number
  readonly bestOperationStreak: number
  readonly achievements: readonly AchievementId[]
  readonly tutorialCompleted: boolean
  readonly selectedMapId: MapId
  readonly mapSeed: number
  readonly sectorRecords: Record<MapId, SectorRecord>
  readonly enemyRecords: Record<EnemyKind, EnemyRecord>
  readonly preferredLoadout: LoadoutId
  readonly completedChapters: readonly MapId[]
  readonly selectedRunMode: RunMode
  readonly bestEndlessWave: number
  readonly bestEndlessScore: number
  readonly lastChallengeId: string | null
  readonly bestChallengeScore: number
}

export interface SectorRecord {
  readonly clears: number
  readonly bestStars: number
  readonly bestCoreIntegrity: number
  readonly bestWave: number
  readonly bestScore: number
}

export interface EnemyRecord {
  readonly kills: number
  readonly breaches: number
}

export interface RunResult {
  readonly mapId: MapId
  readonly victory: boolean
  readonly stars: number
  readonly coreIntegrity: number
  readonly wave: number
  readonly xpAwarded: number
  readonly score: number
  readonly rank: PerformanceRank
  readonly runKills: number
  readonly earlyWaveBonus: number
  readonly contractId: ContractId | null
  readonly contractCompleted: boolean
  readonly contractXp: number
  readonly contractStreak: number
  readonly operationId: string
  readonly operationCompleted: boolean
  readonly operationXp: number
  readonly unlockedAchievements: readonly AchievementId[]
  readonly achievementXp: number
  readonly firstChapterClear: boolean
  readonly chapterRewardXp: number
  readonly chapterReserveBonus: number
  readonly runMode: RunMode
  readonly dailyChallengeId: string | null
  readonly dailyChallengeCompleted: boolean
  readonly dailyChallengeXp: number
  readonly newEndlessBest: boolean
  readonly telemetry: RunTelemetry
}

function emptySectorRecords(): Record<MapId, SectorRecord> {
  return Object.fromEntries(MAP_IDS.map((id) => [id, { clears: 0, bestStars: 0, bestCoreIntegrity: 0, bestWave: 1, bestScore: 0 }])) as Record<MapId, SectorRecord>
}

function emptyEnemyRecords(): Record<EnemyKind, EnemyRecord> {
  return Object.fromEntries(ENEMY_KINDS.map((kind) => [kind, { kills: 0, breaches: 0 }])) as Record<EnemyKind, EnemyRecord>
}

const DEFAULT_PROFILE: PlayerProfile = {
  level: 1,
  xp: 0,
  totalKills: 0,
  runs: 0,
  wins: 0,
  bestWave: 1,
  bestScore: 0,
  completedContracts: 0,
  contractStreak: 0,
  bestContractStreak: 0,
  lastOperationId: null,
  operationStreak: 0,
  bestOperationStreak: 0,
  achievements: [],
  tutorialCompleted: false,
  selectedMapId: 'GENESIS_NETWORK',
  mapSeed: 104729,
  sectorRecords: emptySectorRecords(),
  enemyRecords: emptyEnemyRecords(),
  preferredLoadout: 'BALANCED_GRID',
  completedChapters: [],
  selectedRunMode: 'CAMPAIGN',
  bestEndlessWave: 0,
  bestEndlessScore: 0,
  lastChallengeId: null,
  bestChallengeScore: 0,
}

const KILL_XP: Record<EnemyKind, number> = {
  BOT: 4,
  PAPER_HAND: 5,
  FUD_BOT: 12,
  LIQUIDATOR: 20,
  WHALE: 100,
}

class MetaProgress {
  private state: PlayerProfile = this.load()

  get profile(): PlayerProfile { return this.state }
  get selectedMap() { return MAP_DEFINITIONS[this.state.selectedMapId] }
  get veteranCapitalBonus(): number { return Math.min(2_000, (this.state.level - 1) * 250) }
  get campaignCapitalBonus(): number { return this.state.completedChapters.reduce((sum, id) => sum + CAMPAIGN_CHAPTERS[id].reserveBonus, 0) }
  get startingCapitalBonus(): number { return this.veteranCapitalBonus + this.campaignCapitalBonus }
  xpForNextLevel(level = this.state.level): number { return 100 + (level - 1) * 75 }

  awardKill(kind: EnemyKind): void {
    this.update({
      totalKills: this.state.totalKills + 1,
      xp: this.state.xp + KILL_XP[kind],
      enemyRecords: {
        ...this.state.enemyRecords,
        [kind]: { ...this.state.enemyRecords[kind], kills: this.state.enemyRecords[kind].kills + 1 },
      },
    })
  }

  recordBreach(kind: EnemyKind): void {
    this.update({
      enemyRecords: {
        ...this.state.enemyRecords,
        [kind]: { ...this.state.enemyRecords[kind], breaches: this.state.enemyRecords[kind].breaches + 1 },
      },
    })
  }

  completeRun(victory: boolean, wave: number, coreIntegrity: number, runKills = 0, earlyWaveBonus = 0, contractId: ContractId | null = null, telemetry: RunTelemetry = emptyRunTelemetry()): RunResult {
    const mapId = this.state.selectedMapId
    const map = MAP_DEFINITIONS[mapId]
    const runMode = this.state.selectedRunMode
    const dailyChallenge = getDailyChallenge()
    const chapter = CAMPAIGN_CHAPTERS[mapId]
    const previous = this.state.sectorRecords[mapId]
    const firstChapterClear = runMode === 'CAMPAIGN' && victory && !this.state.completedChapters.includes(mapId)
    const chapterRewardXp = firstChapterClear ? chapter.rewardXp : 0
    const chapterReserveBonus = firstChapterClear ? chapter.reserveBonus : 0
    const stars = victory ? coreIntegrity >= map.masteryCoreThresholds[1] ? 3 : coreIntegrity >= map.masteryCoreThresholds[0] ? 2 : 1 : 0
    const contract = contractId ? CONTRACTS[contractId] : null
    const contractCompleted = contract?.evaluate({ victory, coreIntegrity, runKills, earlyWaveBonus }) ?? false
    const contractXp = contractCompleted ? contract?.rewardXp ?? 0 : 0
    const operation = getDailyOperation()
    const operationCompleted = contractCompleted && contractId === operation.contractId && this.state.lastOperationId !== operation.id
    const operationXp = operationCompleted ? operation.bonusXp : 0
    const contractStreak = contractId ? contractCompleted ? this.state.contractStreak + 1 : 0 : this.state.contractStreak
    const baseScore = (victory ? 3_500 : wave * 450) + coreIntegrity * 20 + runKills * 35 + earlyWaveBonus * 2 + telemetry.encounterBonus * 2 + telemetry.riskyEncountersCleared * 400 + telemetry.peakThreatLevel * 300 + telemetry.threatEscalations * 250 + stars * 500 + (firstChapterClear ? chapter.scoreBonus : 0) + (contractCompleted ? contract?.scoreBonus ?? 0 : 0) + (operationCompleted ? operation.scoreBonus : 0)
    const score = Math.round(baseScore * (runMode === 'DAILY_CHALLENGE' ? dailyChallenge.scoreMultiplier : 1))
    const rank = performanceRank(score)
    const unlockedAchievements = ACHIEVEMENT_IDS.filter((id) => !this.state.achievements.includes(id) && (
      (id === 'FIRST_CONTRACT' && contractCompleted)
      || (id === 'CONTRACT_CHAIN' && contractStreak >= 3)
      || (id === 'DAILY_OPERATOR' && operationCompleted)
      || (id === 'S_RANK' && rank === 'S')
      || (id === 'SCORE_BREAKER' && score >= 10_000)
    ))
    const achievementXp = unlockedAchievements.reduce((sum, id) => sum + ACHIEVEMENTS[id].rewardXp, 0)
    const dailyChallengeCompleted = runMode === 'DAILY_CHALLENGE' && victory && this.state.lastChallengeId !== dailyChallenge.id
    const dailyChallengeXp = dailyChallengeCompleted ? dailyChallenge.xpBonus : 0
    const completionXp = (victory ? 120 + stars * 40 : Math.max(20, wave * 18)) + contractXp + operationXp + achievementXp + chapterRewardXp + dailyChallengeXp
    const newEndlessBest = runMode === 'ENDLESS' && wave > this.state.bestEndlessWave
    const operationStreak = operationCompleted ? (isConsecutiveOperation(this.state.lastOperationId, operation.dateKey) ? this.state.operationStreak + 1 : 1) : this.state.operationStreak
    const sectorRecords = {
      ...this.state.sectorRecords,
      [mapId]: {
        clears: previous.clears + (victory ? 1 : 0),
        bestStars: Math.max(previous.bestStars, stars),
        bestCoreIntegrity: Math.max(previous.bestCoreIntegrity, victory ? coreIntegrity : 0),
        bestWave: Math.max(previous.bestWave, wave),
        bestScore: Math.max(previous.bestScore, score),
      },
    }
    this.update({
      runs: this.state.runs + 1,
      wins: this.state.wins + (victory ? 1 : 0),
      bestWave: Math.max(this.state.bestWave, wave),
      bestScore: Math.max(this.state.bestScore, score),
      completedContracts: this.state.completedContracts + (contractCompleted ? 1 : 0),
      contractStreak,
      bestContractStreak: Math.max(this.state.bestContractStreak, contractStreak),
      lastOperationId: operationCompleted ? operation.id : this.state.lastOperationId,
      operationStreak,
      bestOperationStreak: Math.max(this.state.bestOperationStreak, operationStreak),
      achievements: [...this.state.achievements, ...unlockedAchievements],
      xp: this.state.xp + completionXp,
      sectorRecords,
      completedChapters: firstChapterClear ? [...this.state.completedChapters, mapId] : this.state.completedChapters,
      bestEndlessWave: runMode === 'ENDLESS' ? Math.max(this.state.bestEndlessWave, wave) : this.state.bestEndlessWave,
      bestEndlessScore: runMode === 'ENDLESS' ? Math.max(this.state.bestEndlessScore, score) : this.state.bestEndlessScore,
      lastChallengeId: dailyChallengeCompleted ? dailyChallenge.id : this.state.lastChallengeId,
      bestChallengeScore: runMode === 'DAILY_CHALLENGE' ? Math.max(this.state.bestChallengeScore, score) : this.state.bestChallengeScore,
    })
    return { mapId, victory, stars, coreIntegrity, wave, xpAwarded: completionXp, score, rank, runKills, earlyWaveBonus, contractId, contractCompleted, contractXp, contractStreak, operationId: operation.id, operationCompleted, operationXp, unlockedAchievements, achievementXp, firstChapterClear, chapterRewardXp, chapterReserveBonus, runMode, dailyChallengeId: runMode === 'DAILY_CHALLENGE' ? dailyChallenge.id : null, dailyChallengeCompleted, dailyChallengeXp, newEndlessBest, telemetry }
  }

  completeTutorial(): void {
    if (!this.state.tutorialCompleted) this.update({ tutorialCompleted: true, xp: this.state.xp + 25 })
  }

  isMapUnlocked(id: MapId): boolean { return this.state.level >= MAP_DEFINITIONS[id].requiredLevel }

  selectMap(id: MapId): boolean {
    if (!this.isMapUnlocked(id)) return false
    this.update({ selectedMapId: id })
    return true
  }

  selectLoadout(id: LoadoutId): void {
    this.update({ preferredLoadout: id })
  }

  selectRunMode(mode: RunMode): void {
    this.update({ selectedRunMode: mode })
  }

  rerollMap(): void {
    this.update({ mapSeed: Math.floor(Math.random() * 900_000) + 100_000 })
  }

  debugAddXp(amount = 100): void { this.update({ xp: this.state.xp + amount }) }

  exportProfile(): string {
    return JSON.stringify({ version: 1, exportedAt: new Date().toISOString(), profile: this.state }, null, 2)
  }

  importProfile(raw: string): boolean {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (isRecord(parsed) && 'version' in parsed && parsed.version !== 1) return false
      const candidate = isRecord(parsed) && 'profile' in parsed ? parsed.profile : parsed
      if (!isRecord(candidate)) return false
      this.state = this.normalize(candidate)
      this.save()
      gameBridge.emit('profileChanged', this.state)
      return true
    } catch {
      return false
    }
  }

  resetProfile(): void {
    this.state = this.normalize({})
    this.save()
    gameBridge.emit('profileChanged', this.state)
  }

  private update(patch: Partial<PlayerProfile>): void {
    let next = { ...this.state, ...patch }
    let required = this.xpForNextLevel(next.level)
    while (next.xp >= required) {
      next = { ...next, xp: next.xp - required, level: next.level + 1 }
      required = this.xpForNextLevel(next.level)
    }
    this.state = next
    this.save()
    gameBridge.emit('profileChanged', this.state)
  }

  private load(): PlayerProfile {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY)
      return saved ? this.normalize(JSON.parse(saved)) : this.normalize({})
    } catch {
      return this.normalize({})
    }
  }

  private normalize(value: unknown): PlayerProfile {
    const parsed = isRecord(value) ? value as Partial<PlayerProfile> : {}
    const number = (candidate: unknown, fallback: number, maximum = Number.MAX_SAFE_INTEGER) => typeof candidate === 'number' && Number.isFinite(candidate) ? Math.min(maximum, Math.max(0, Math.floor(candidate))) : fallback
    const defaults = emptySectorRecords()
    const savedSectors: Record<string, unknown> = isRecord(parsed.sectorRecords) ? parsed.sectorRecords : {}
    const sectorRecords = Object.fromEntries(MAP_IDS.map((id) => {
      const record = isRecord(savedSectors[id]) ? savedSectors[id] : {}
      return [id, {
        clears: number(record.clears, defaults[id].clears),
        bestStars: number(record.bestStars, defaults[id].bestStars, 3),
        bestCoreIntegrity: number(record.bestCoreIntegrity, defaults[id].bestCoreIntegrity, 100),
        bestWave: Math.max(1, number(record.bestWave, defaults[id].bestWave)),
        bestScore: number(record.bestScore, defaults[id].bestScore),
      }]
    })) as Record<MapId, SectorRecord>
    const enemyDefaults = emptyEnemyRecords()
    const savedEnemies: Record<string, unknown> = isRecord(parsed.enemyRecords) ? parsed.enemyRecords : {}
    const enemyRecords = Object.fromEntries(ENEMY_KINDS.map((kind) => {
      const record = isRecord(savedEnemies[kind]) ? savedEnemies[kind] : {}
      return [kind, { kills: number(record.kills, enemyDefaults[kind].kills), breaches: number(record.breaches, enemyDefaults[kind].breaches) }]
    })) as Record<EnemyKind, EnemyRecord>
    const completedChapters = Array.isArray(parsed.completedChapters)
      ? [...new Set(parsed.completedChapters.filter((id): id is MapId => typeof id === 'string' && MAP_IDS.includes(id as MapId)))]
      : CAMPAIGN_ORDER.filter((id) => sectorRecords[id].clears > 0)
    const achievements = Array.isArray(parsed.achievements)
      ? [...new Set(parsed.achievements.filter((id): id is AchievementId => typeof id === 'string' && ACHIEVEMENT_IDS.includes(id as AchievementId)))]
      : []
    const level = Math.max(1, number(parsed.level, 1, 999))
    return {
      ...DEFAULT_PROFILE,
      level, xp: number(parsed.xp, 0, 1_000_000), totalKills: number(parsed.totalKills, 0, 1_000_000_000), runs: number(parsed.runs, 0, 1_000_000_000), wins: number(parsed.wins, 0, 1_000_000_000),
      bestWave: Math.max(1, number(parsed.bestWave, 1, 100_000)), bestScore: number(parsed.bestScore, 0, 1_000_000_000_000), completedContracts: number(parsed.completedContracts, 0, 1_000_000_000), contractStreak: number(parsed.contractStreak, 0, 1_000_000), bestContractStreak: number(parsed.bestContractStreak, 0, 1_000_000),
      lastOperationId: typeof parsed.lastOperationId === 'string' ? parsed.lastOperationId.slice(0, 100) : null, operationStreak: number(parsed.operationStreak, 0, 1_000_000), bestOperationStreak: number(parsed.bestOperationStreak, 0, 1_000_000), achievements,
      tutorialCompleted: parsed.tutorialCompleted === true, selectedMapId: MAP_IDS.includes(parsed.selectedMapId as MapId) ? parsed.selectedMapId as MapId : DEFAULT_PROFILE.selectedMapId,
      mapSeed: Math.max(1, number(parsed.mapSeed, DEFAULT_PROFILE.mapSeed, 999_999)), sectorRecords, enemyRecords,
      preferredLoadout: LOADOUT_IDS.includes(parsed.preferredLoadout as LoadoutId) ? parsed.preferredLoadout as LoadoutId : DEFAULT_PROFILE.preferredLoadout,
      completedChapters, selectedRunMode: parsed.selectedRunMode === 'ENDLESS' || parsed.selectedRunMode === 'DAILY_CHALLENGE' ? parsed.selectedRunMode : 'CAMPAIGN',
      bestEndlessWave: number(parsed.bestEndlessWave, 0, 100_000), bestEndlessScore: number(parsed.bestEndlessScore, 0, 1_000_000_000_000), lastChallengeId: typeof parsed.lastChallengeId === 'string' ? parsed.lastChallengeId.slice(0, 100) : null, bestChallengeScore: number(parsed.bestChallengeScore, 0, 1_000_000_000_000),
    }
  }

  private save(): void {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)) } catch { /* storage can be unavailable */ }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export const metaProgress = new MetaProgress()

function performanceRank(score: number): PerformanceRank {
  if (score >= 9_500) return 'S'
  if (score >= 7_500) return 'A'
  if (score >= 5_500) return 'B'
  if (score >= 3_000) return 'C'
  return 'D'
}
